// src/components/JobResultListener.js
import { useEffect, useRef } from 'react';
import { useData } from '../context/DataContext';
import { useJobs } from '../context/jobContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { ERROR_CODES } from '../constants/errors';

const JobResultListener = () => {
  const { currentUser } = useAuth();

  // Get methods/state from DataContext
  const {
    activeinfluencerId,
    lastQuery,
    runSingleQuery,
    runMultipleQuery,
    setQueryResults,
    setError,
    fetchSocialMediaConnections,
    isOffline,
    setAggregatedMarketFit,
    setAggregatedPublicationSummary,
    setIndividualMarketFitAnalysis,
    setPublicationAiSummary,
    getInfluencerDetails, // Ensure this is exposed or imported
    fetchSystemInvoices,
  } = useData();

  // Get methods/state from JobContext
  const {
    contentAnalysisJob,
    discoveryJob,
    agentJobHistory,
    clearDiscoveryJobError,
    clearContentAnalysisJobError,
  } = useJobs();

  // --- LOGIC 1: AGENT JOB AUTO-REFRESH (From Previous Turn) ---
  const finishedJobIdsRef = useRef(new Set());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!currentUser || !agentJobHistory || agentJobHistory.length === 0)
      return;

    if (!isInitializedRef.current) {
      agentJobHistory.forEach((job) => {
        if (['completed', 'failed', 'finished'].includes(job.status)) {
          finishedJobIdsRef.current.add(job.id);
        }
      });
      isInitializedRef.current = true;
      return;
    }

    // 1. Define our flags (The established pattern)
    let shouldRefreshInfluencerData = false;
    let shouldRefreshBillingData = false; // <--- NEW FLAG

    // 2. Synchronous Loop: Only evaluate state and set flags
    agentJobHistory.forEach((job) => {
      const isFinished = ['completed', 'failed', 'finished'].includes(
        job.status,
      );

      if (isFinished && !finishedJobIdsRef.current.has(job.id)) {
        finishedJobIdsRef.current.add(job.id);

        if (job.jobType === 'DISPATCH_INVOICES') {
          // Toast
          if (job.status === 'completed')
            toast.success(`Success: Statements delivered.`);
          else toast.error(`Error: Dispatch failed.`);

          // SET THE FLAG (Do not execute async here)
          shouldRefreshBillingData = true;
        } else {
          shouldRefreshInfluencerData = true;
        }
      }
    });

    // 3. Asynchronous Execution (Outside the loop, strictly controlled)

    // --- NEW BILLING REFRESH ---
    if (shouldRefreshBillingData) {
      const refreshBilling = async () => {
        try {
          await fetchSystemInvoices();
          console.log('[JobListener] Invoice table refreshed successfully.');
        } catch (e) {
          console.error('[JobListener] Failed to refresh invoices:', e);
        }
      };
      refreshBilling();
    }

    if (shouldRefreshInfluencerData && lastQuery) {
      const refreshData = async () => {
        try {
          let results = [];
          if (lastQuery.type === 'single') {
            results = await runSingleQuery(lastQuery.params);
          } else if (lastQuery.type === 'multiple') {
            results = await runMultipleQuery(lastQuery.params);
          }
          if (Array.isArray(results)) {
            setQueryResults(results);
            toast.info('Data updated with latest Agent results.');
          }
        } catch (e) {
          console.error('[JobListener] Auto-refresh failed:', e);
        }
      };
      refreshData();
    }
  }, [
    agentJobHistory,
    currentUser,
    lastQuery,
    runSingleQuery,
    runMultipleQuery,
    setQueryResults,
    fetchSystemInvoices,
  ]);

  // --- LOGIC 2: CONTENT ANALYSIS JOB RESULT PROCESSING ---
  useEffect(() => {
    // 1. Guard Clause
    if (
      contentAnalysisJob.jobStatus !== 'finished' ||
      !contentAnalysisJob.results ||
      !contentAnalysisJob.influencerId ||
      isOffline
    )
      return;

    const { influencerId, results } = contentAnalysisJob;
    const {
      analysis_results,
      aggregated_market_fit_analysis,
      aggregated_publication_summary,
    } = results;

    // 2. Update Aggregated Dashboards (Only if viewing this influencer)
    if (influencerId === activeinfluencerId) {
      if (aggregated_market_fit_analysis) {
        setAggregatedMarketFit(aggregated_market_fit_analysis);
      }
      if (aggregated_publication_summary) {
        setAggregatedPublicationSummary(aggregated_publication_summary);
      }
    }

    // 3. Update Individual Analysis Data (Batch Update)
    if (analysis_results && typeof analysis_results === 'object') {
      // A. Market Fit — Dispatch per video to match reducer expectation
      Object.entries(analysis_results).forEach(([vid, res]) => {
        if (res.market_fit) {
          setIndividualMarketFitAnalysis({
            videoId: vid,
            analysis: res.market_fit,
          });
        }
      });

      // B. Content Quality (Array for Reducer)
      // The reducer expects: [{ videoId, analysis }, ...]
      const qualityArray = Object.entries(analysis_results)
        .filter(([_, res]) => res.content_quality?.analysis) // Filter only valid ones
        .map(([vid, res]) => ({
          videoId: vid,
          analysis: res.content_quality.analysis,
        }));

      if (qualityArray.length > 0) {
        // Pass the array directly. The reducer logic you shared handles Array.isArray()
        setPublicationAiSummary(qualityArray);
      }
    } else if (analysis_results != null) {
      // Optional: warn about unexpected type
      console.warn('Unexpected analysis_results format:', analysis_results);
    }

    // 4. Refetch & Update Influencer Details (Status/Scores)
    // We use the exposed 'getInfluencerDetails' wrapper from DataContext
    const refreshInfluencerData = async () => {
      try {
        const response = await getInfluencerDetails(influencerId);

        if (response.status && response.data) {
          console.log(
            `[JobListener] Refreshed details for influencer ${influencerId}`,
          );

          // Update the global queryResults list with the fresh data
          setQueryResults((prevResults) => {
            const safeList = Array.isArray(prevResults) ? prevResults : [];
            return safeList.map((inf) =>
              inf.influencerId === influencerId ? response.data : inf,
            );
          });
        }
      } catch (e) {
        console.error(
          `[JobListener] Failed to refresh influencer ${influencerId} details:`,
          e,
        );
      }
    };

    refreshInfluencerData();
  }, [
    contentAnalysisJob,
    activeinfluencerId,
    isOffline,
    setAggregatedMarketFit,
    setAggregatedPublicationSummary,
    setIndividualMarketFitAnalysis,
    setPublicationAiSummary,
    getInfluencerDetails,
    setQueryResults,
  ]);

  // --- LOGIC 3: JOB FAILURE LISTENER (Moved from DataContext) ---
  useEffect(() => {
    if (isOffline) return;

    const handleFailure = async (job, clearJobError) => {
      if (job.jobStatus === 'failed' && job.error) {
        const errorCode = job.error.code;

        const actionableCodes = [
          ERROR_CODES.OAUTH_TOKEN_REVOKED,
          ERROR_CODES.OAUTH_NOT_CONFIGURED,
          'EMAIL_NOT_VERIFIED',
        ];

        if (actionableCodes.includes(errorCode)) {
          console.log(
            `[JobListener] Promoting critical error ${errorCode} to Global Modal.`,
          );
          setError(job.error); // This triggers ErrorDisplayModal via DataContext

          if (currentUser) {
            console.warn(
              '[JobListener] Auth error detected. Refreshing connections.',
              job.error,
            );
            await fetchSocialMediaConnections(currentUser.uid);
          }

          // IMPORTANT: We clear the JOB error because the GLOBAL error is now handling it.
          // This prevents the Red Box and the Modal from showing the same thing.
          clearJobError();
        } else {
          // For non-actionable errors, we leave them in the JobContext
          // so the PublicationPage's Red Panel can show the specific details.
          console.log(
            `[JobListener] Task-level error detected: ${errorCode}. Handled by page panel.`,
          );
        }
      }
    };

    handleFailure(discoveryJob, clearDiscoveryJobError);
    handleFailure(contentAnalysisJob, clearContentAnalysisJobError);
  }, [
    discoveryJob,
    contentAnalysisJob,
    isOffline,
    currentUser,
    setError,
    fetchSocialMediaConnections,
    clearDiscoveryJobError,
    clearContentAnalysisJobError,
  ]);

  // This component renders nothing
  return null;
};

export default JobResultListener;
